import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { Badge, Card, cn, Icon, type IconName } from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  businessDate,
  format,
  formatDate,
  fromColumn,
  sum,
  type Money,
} from '@evogard/domain';
import type {
  AwardLine,
  Period,
  WalletInsights,
  WalletMixSlice,
  WalletOpenBoleto,
  WalletOpenBoletos,
} from '@evogard/contract';
import { compactMoney } from './StatementChart.js';
import { periodLabel } from './common.js';

/** O panorama que respondeu — o `sem_vinculo` já é dito pelo painel. */
export type MeasuredInsights = Extract<WalletInsights, { estado: 'medido' }>;

/** Os boletos em aberto que responderam. */
export function measuredOpenBoletos(
  data: WalletOpenBoletos | undefined,
): Extract<WalletOpenBoletos, { estado: 'medido' }> | undefined {
  return data !== undefined && data.estado === 'medido' ? data : undefined;
}

export function measuredInsights(
  data: WalletInsights | undefined,
): MeasuredInsights | undefined {
  return data !== undefined && data.estado === 'medido' ? data : undefined;
}

/**
 * O total de um lado da competência, ou `null` quando alguma premiação recusou.
 *
 * A mesma regra de `WalletTotal`: somar só as que apuraram produziria o número
 * que alguém confere contra o contracheque com cara de completo.
 */
export function sideTotal(
  categories: readonly { total: string | null }[] | undefined,
): Money | null {
  if (categories === undefined || categories.length === 0) return null;
  if (!categories.every((c) => c.total !== null)) return null;
  return sum(...categories.map((c) => fromColumn(c.total)!));
}

/* ------------------------------------------------------------------------- *
 * O desenho das Métricas (29/10/2026)
 *
 * O dono mandou três telas de referência (`referencia/metrica1..3.jpeg`) e
 * pediu que elas fossem o padrão: cada bloco é um cartão com ícone, título e a
 * frase do recorte, e a figura mora dentro dele. Seis blocos, nesta ordem —
 * Valores apurados, Evolução, Origem, Boletos, Veículos (que entrou no mesmo
 * dia, sobre o desenho dos Boletos) e Distribuição da Carteira. No
 * mesmo dia o dono enxugou os textos de apoio e tirou "Planejar a
 * recorrência" (a calculadora e o `walletPlan`); a rota `walletForecast` e o
 * `planBaseline` de `walletInsights` ficaram no contrato, sem tela.
 *
 * As figuras pequenas (as roscas e as barras deitadas) são SVG e `div`, e não o
 * `Chart`: cada número delas está escrito ao lado, em texto, e o Recharts
 * pesaria a tela para desenhar o que três linhas de CSS desenham. A Evolução e
 * os Boletos, que têm eixo, são o `Chart` da casa.
 * ------------------------------------------------------------------------- */

/** O título de um bloco das Métricas: o ícone na cor do bloco, e o nome. */
function BlockTitle({
  icon,
  tone,
  children,
}: {
  icon: IconName;
  /** A cor do ícone — a do assunto do bloco, como nas referências. */
  tone: string;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-2 text-sm font-semibold">
      <Icon nome={icon} size={16} className={tone} />
      {children}
    </span>
  );
}

/** Um pedaço da rosca: o valor e a classe do traço (`stroke-…`). */
interface DonutSegment {
  key: string;
  value: number;
  stroke: string;
}

/**
 * A ROSCA — os pedaços de um todo, com um respiro entre eles.
 *
 * Decorativa para o leitor de tela (`aria-hidden`): quem a usa escreve os
 * mesmos números em texto ao lado, e é ali que eles se leem.
 */
function Donut({
  segments,
  size = 168,
  thickness = 26,
  children,
}: {
  segments: readonly DonutSegment[];
  size?: number;
  thickness?: number;
  /** O que vai no furo — o percentual, por exemplo. */
  children?: ReactNode;
}) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((acc, s) => acc + Math.max(s.value, 0), 0);
  const drawn = segments.filter((s) => s.value > 0);
  // um pedaço só fecha o anel; o respiro abriria um corte que não divide nada
  const gap = drawn.length > 1 ? 3 : 0;
  const arcs = drawn.reduce<{ segment: DonutSegment; start: number; length: number }[]>(
    (acc, segment) => {
      const previous = acc[acc.length - 1];
      const start = previous === undefined ? 0 : previous.start + previous.length;
      return [
        ...acc,
        { segment, start, length: (segment.value / total) * circumference },
      ];
    },
    [],
  );

  return (
    <div
      className="relative grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={thickness}
          className="stroke-superficie-suave"
        />
        {arcs.map(({ segment, start, length }) => (
          <circle
            key={segment.key}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={thickness}
            strokeDasharray={`${Math.max(length - gap, 0)} ${circumference}`}
            strokeDashoffset={-start}
            className={segment.stroke}
          />
        ))}
      </svg>
      {children !== undefined && (
        <div className="absolute inset-0 grid place-items-center text-center">
          {children}
        </div>
      )}
    </div>
  );
}

/** O espaço de um número que ainda não chegou. */
function Pending({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded-borda bg-borda-suave', className)}
    />
  );
}

/** `0,75` → `75%`, inteiro: o percentual de uma parte, para ler de relance. */
function share(part: number, total: number): string {
  return `${Math.round((part / total) * 100).toLocaleString('pt-BR')}%`;
}

/** O ladrilho tingido de cada lado — o que entrou e o que está em aberto. */
const VALUE_TILES = {
  blue: { box: 'border-fase-azul/25 bg-fase-azul-suave', value: 'text-fase-azul' },
  green: { box: 'border-fase-verde/25 bg-fase-verde-suave', value: 'text-fase-verde' },
  orange: {
    box: 'border-fase-laranja/25 bg-fase-laranja-suave',
    value: 'text-fase-laranja',
  },
} as const;

function ValueTile({
  tone,
  label,
  value,
  support,
  loading,
}: {
  tone: keyof typeof VALUE_TILES;
  label: string;
  value: string;
  support: ReactNode;
  loading: boolean;
}) {
  return (
    <div className={cn('rounded-xl border px-4 py-3', VALUE_TILES[tone].box)}>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-texto-suave">
        {label}
      </p>
      {loading ? (
        <Pending className="mt-1.5 h-6 w-36" />
      ) : (
        <p
          className={cn('mt-0.5 text-xl font-bold tabular-nums', VALUE_TILES[tone].value)}
        >
          {value}
        </p>
      )}
      {/* carregando, o nulo ainda não é "não fechou": é "não chegou" */}
      <p className="mt-0.5 text-xs text-texto-suave">{loading ? ' ' : support}</p>
    </div>
  );
}

/**
 * VALORES APURADOS — o que já entrou e o que está em aberto na competência.
 *
 * A rosca é a parte de cada lado na soma dos dois, e o furo diz quanto já
 * entrou. A soma não aparece como número desde 29/10/2026 (o "total geral
 * apurado" saiu, pedido do dono): ela é teto — o lado em aberto é BRUTO, a
 * direta não tem lado em aberto (`omittedDirect`) e o boleto pode ser pago
 * depois da competência —, e só vive aqui como o todo dos percentuais.
 *
 * O TICKET MÉDIO (03/11/2026, pedido do dono) é o valor dos boletos pagos
 * dividido pela quantidade deles — `walletInsights.ticket.average`, do Extrato
 * gravado, com cada boleto contado uma vez. É o valor do BOLETO, e não o da
 * premiação, e não entra na rosca: ela é a dos dois lados apurados. Até este
 * dia ele morava no Pago do card Boletos.
 */
export function WalletValues({
  guaranteed,
  atRisk,
  ticket,
  month,
  loading,
  ticketLoading,
}: {
  guaranteed: Money | null;
  atRisk: Money | null;
  ticket: MeasuredInsights['ticket'] | undefined;
  month: Period;
  loading: boolean;
  /** O ticket vem de outra leitura (`walletInsights`), e chega no tempo dela. */
  ticketLoading: boolean;
}) {
  const whole = guaranteed !== null && atRisk !== null ? sum(guaranteed, atRisk) : null;
  const missing = 'uma premiação não fechou — veja o Início';
  const measured = whole !== null && whole > 0;
  const support = (side: Money | null) =>
    side === null
      ? missing
      : measured
        ? `${share(side, whole)} do total apurado`
        : 'nada apurado ainda';

  return (
    <Card
      title={
        <BlockTitle icon="grafico" tone="text-fase-verde">
          Valores apurados
        </BlockTitle>
      }
      descricao={periodLabel(month)}
    >
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-stretch">
        <div className="grid place-items-center px-4 py-2">
          <Donut
            segments={
              measured
                ? [
                    { key: 'recebido', value: guaranteed!, stroke: 'stroke-fase-verde' },
                    { key: 'aberto', value: atRisk!, stroke: 'stroke-fase-laranja' },
                  ]
                : []
            }
          >
            <div>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-texto-suave">
                Recebido
              </p>
              {loading ? (
                <Pending className="mx-auto mt-1 h-6 w-14" />
              ) : (
                <p className="text-xl font-bold tabular-nums text-fase-verde">
                  {measured ? share(guaranteed!, whole) : '—'}
                </p>
              )}
            </div>
          </Donut>
        </div>

        <div className="flex w-full min-w-0 flex-1 flex-col justify-center gap-2.5">
          <ValueTile
            tone="green"
            label="Total acumulado"
            loading={loading}
            value={guaranteed === null ? '—' : format(guaranteed)}
            support={support(guaranteed)}
          />
          <ValueTile
            tone="orange"
            label="Total em aberto"
            loading={loading}
            value={atRisk === null ? '—' : format(atRisk)}
            support={support(atRisk)}
          />
          <ValueTile
            tone="blue"
            label="Ticket médio"
            loading={ticketLoading}
            value={ticket?.average == null ? '—' : format(fromColumn(ticket.average)!)}
            support={
              // sem leitura, o erro já está no alto da tela
              ticket === undefined
                ? ' '
                : ticket.boletos === 0
                  ? 'nenhum boleto pago ainda'
                  : `${ticket.boletos.toLocaleString('pt-BR')} boleto(s) pago(s)`
            }
          />
        </div>
      </div>
    </Card>
  );
}

/** Um dia da Evolução: o que entrou nele e o acumulado até ele. */
interface EvolutionDay {
  day: number;
  amount: Money;
  accumulated: Money;
}

/**
 * A EVOLUÇÃO — o dia a dia da competência, acumulado (29/10/2026).
 *
 * Até este dia a área era mês a mês, pelos doze meses do Extrato; o dono
 * pediu o dia a dia do mês do filtro, como a referência. A conta continua
 * sendo o Extrato gravado (`walletInsights.daily`): cada dia soma as entradas
 * pagas nele, e a curva acumula — o último ponto é o total do Extrato no mês,
 * menos o que não tem dia.
 *
 * ## O que não tem dia fica fora da curva
 *
 * A faixa e o produto por placa entram no Extrato sem data de pagamento.
 * Pô-los no dia 1 ou no último inventaria um salto. Até 29/10/2026 uma frase
 * no cabeçalho dizia quanto ficou de fora; o dono pediu para tirá-la.
 *
 * O dia que ainda não chegou não está na figura: no mês corrente a curva para
 * em hoje, e uma linha reta até o dia 31 diria que nada mais vai entrar.
 *
 * Sem "Ver tabela" por pedido do dono (29/10/2026): a dica de cada ponto tem
 * o dia, o que entrou e o acumulado, e o Extrato é a lista linha a linha.
 */
export function WalletEvolution({
  insights,
  month,
  loading,
  error,
}: {
  insights: MeasuredInsights | undefined;
  month: Period;
  loading: boolean;
  error: Error | null;
}) {
  const days = (insights?.daily.days ?? []).reduce<EvolutionDay[]>((acc, d) => {
    const amount = fromColumn(d.amount)!;
    const before = acc[acc.length - 1]?.accumulated ?? (0 as Money);
    return [...acc, { day: d.day, amount, accumulated: sum(before, amount) }];
  }, []);

  return (
    <Chart<EvolutionDay>
      kind="linha"
      area
      withoutTable
      title={
        <BlockTitle icon="tendencia" tone="text-fase-verde">
          Evolução
        </BlockTitle>
      }
      actions={<Badge tone="sucesso">{monthName(month)}</Badge>}
      label={`O que entrou na carteira dia a dia, acumulado, em ${periodLabel(month)}`}
      data={days}
      loading={loading}
      error={error}
      empty={`Nenhum dia de ${periodLabel(month)} para mostrar ainda.`}
      category={(d) => String(d.day).padStart(2, '0')}
      tooltip={({ item }) => <DayReport day={item} month={month} />}
      series={[
        {
          key: 'accumulated',
          label: 'Acumulado',
          color: 'fase-verde',
          value: (d) => d.accumulated,
        },
      ]}
      format={(cents) => format(cents as Money)}
      axisFormat={compactMoney}
      height={240}
    />
  );
}

/** `2025-11` → `Novembro` — o selo do mês da Evolução. */
function monthName(month: string): string {
  const name = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** A dica de um dia da evolução: a data, o que entrou nele e o acumulado. */
function DayReport({ day, month }: { day: EvolutionDay; month: Period }) {
  return (
    <>
      <p className="font-semibold text-texto">
        {formatDate(`${month}-${String(day.day).padStart(2, '0')}`)}
      </p>
      <dl className="mt-1.5 grid grid-cols-[auto_auto] gap-x-3 gap-y-1">
        <dt className="text-texto-suave">No dia</dt>
        <dd className="text-right font-medium text-texto tabular-nums">
          {format(day.amount)}
        </dd>
        <dt className="text-texto-suave">Acumulado</dt>
        <dd className="text-right font-semibold text-texto tabular-nums">
          {format(day.accumulated)}
        </dd>
      </dl>
    </>
  );
}

/** Uma premiação da Origem: o que acumulou e o que está em aberto nela. */
interface OriginRow {
  key: string;
  label: string;
  paid: Money;
  /** Nulo quando a premiação não tem lado em aberto — a direta, que conta placa. */
  open: Money | null;
}

/**
 * A ORIGEM — de que premiação vem o valor, pelos dois lados.
 *
 * Uma linha por premiação, com a barra do acumulado (boleto pago) e a do que
 * está em aberto embaixo. Nenhuma conta nova: as linhas são as dos dois lados
 * que a tela já carregou, com o valor que o servidor apurou.
 *
 * **A premiação recusada não entra, e a tela diz quantas foram.** Ela não tem
 * valor; desenhá-la em zero diria que ela não rendeu nada.
 *
 * **A direta não tem barra em aberto, e não é zero.** No lado `perdendo` ela
 * fica de fora inteira (`omittedDirect`): a placa em aberto ainda pode ativar,
 * e ninguém chamou isso de perda. A barra laranja ausente é essa ausência.
 */
export function WalletOrigin({
  guaranteed,
  atRisk,
  loading,
}: {
  guaranteed: readonly AwardLine[];
  atRisk: readonly AwardLine[];
  loading: boolean;
}) {
  const refused = [...guaranteed, ...atRisk].filter(
    (l) => l.estado === 'recusado',
  ).length;
  const rows = originRows(guaranteed, atRisk);
  const scale = niceScale(Math.max(0, ...rows.flatMap((r) => [r.paid, r.open ?? 0])));
  const position = (value: number) => (scale.top === 0 ? 0 : (value / scale.top) * 100);

  return (
    <Card
      title={
        <BlockTitle icon="bandeira" tone="text-fase-azul">
          Origem
        </BlockTitle>
      }
      descricao={
        refused === 0
          ? undefined
          : `${refused.toLocaleString('pt-BR')} premiação(ões) ficou(aram) de fora — veja o Início`
      }
    >
      {loading ? (
        <Pending className="h-56 w-full" />
      ) : rows.length === 0 ? (
        <p className="grid h-32 place-items-center text-center text-sm text-texto-suave">
          Nenhuma premiação tem valor neste mês ainda.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[minmax(5.5rem,9rem)_minmax(0,1fr)] gap-x-3">
            {rows.map((row) => (
              <Fragment key={row.key}>
                <span className="self-center py-1.5 text-right text-xs leading-tight text-texto-suave">
                  {row.label}
                </span>
                <div className="relative flex flex-col justify-center gap-1 py-1.5">
                  <Gridlines ticks={scale.ticks} position={position} />
                  <OriginBar
                    value={row.paid}
                    width={position(row.paid)}
                    className="bg-fase-verde"
                    label="Acumulado"
                  />
                  {row.open === null ? (
                    <span className="sr-only">Em aberto: não se aplica</span>
                  ) : (
                    <OriginBar
                      value={row.open}
                      width={position(row.open)}
                      className="bg-fase-laranja"
                      label="Em aberto"
                    />
                  )}
                </div>
              </Fragment>
            ))}
            <span />
            <div aria-hidden="true" className="relative mt-1 h-4">
              {scale.ticks.map((tick, i) => (
                <span
                  key={tick}
                  className={cn(
                    'absolute top-0 text-rotulo whitespace-nowrap text-texto-suave tabular-nums',
                    i === 0
                      ? ''
                      : i === scale.ticks.length - 1
                        ? '-translate-x-full'
                        : '-translate-x-1/2',
                  )}
                  style={{ left: `${position(tick)}%` }}
                >
                  {compactMoney(tick)}
                </span>
              ))}
            </div>
          </div>
          <Legend
            items={[
              { label: 'Acumulado', className: 'bg-fase-verde' },
              { label: 'Em aberto', className: 'bg-fase-laranja' },
            ]}
          />
        </div>
      )}
    </Card>
  );
}

/** As premiações dos dois lados, juntas pela identidade, da maior para a menor. */
function originRows(
  guaranteed: readonly AwardLine[],
  atRisk: readonly AwardLine[],
): OriginRow[] {
  // a concessão recebida tem o `id` da premiação do líder: quem concedeu separa
  const keyOf = (l: AwardLine) => `${l.id}:${l.grantedBy?.id ?? ''}`;
  const amountOf = (l: AwardLine) =>
    l.estado === 'apurado' ? fromColumn(l.amount)! : null;
  const open = new Map(atRisk.map((l) => [keyOf(l), amountOf(l)]));
  const rows = new Map<string, OriginRow>();

  for (const line of guaranteed) {
    const paid = amountOf(line);
    if (paid === null) continue;
    rows.set(keyOf(line), {
      key: keyOf(line),
      label: line.title,
      paid,
      open: open.get(keyOf(line)) ?? null,
    });
  }
  // a premiação só com valor em aberto também é origem: ainda não entrou nada
  for (const line of atRisk) {
    const amount = amountOf(line);
    if (amount === null || rows.has(keyOf(line))) continue;
    rows.set(keyOf(line), {
      key: keyOf(line),
      label: line.title,
      paid: 0 as Money,
      open: amount,
    });
  }

  return [...rows.values()]
    .filter((r) => r.paid > 0 || (r.open ?? 0) > 0)
    .sort((a, b) => b.paid - a.paid || (b.open ?? 0) - (a.open ?? 0));
}

/**
 * Um eixo de quatro passos redondos que cobre `max` — `R$ 0`, `R$ 1,5 mil`,
 * `R$ 3 mil`… O passo sai de 1, 1,5, 2, 2,5, 3 e 5 vezes uma potência de dez.
 */
function niceScale(max: number): { top: number; ticks: number[] } {
  if (max <= 0) return { top: 0, ticks: [0] };
  const raw = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step =
    [1, 1.5, 2, 2.5, 3, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ??
    10 * magnitude;
  return { top: step * 4, ticks: [0, 1, 2, 3, 4].map((i) => i * step) };
}

/** As linhas verticais tracejadas do eixo, atrás das barras de uma premiação. */
function Gridlines({
  ticks,
  position,
}: {
  ticks: readonly number[];
  position: (value: number) => number;
}) {
  return (
    <>
      {ticks.slice(1).map((tick) => (
        <span
          key={tick}
          aria-hidden="true"
          className="absolute inset-y-0 border-l border-dashed border-borda-suave"
          style={{ left: `${position(tick)}%` }}
        />
      ))}
    </>
  );
}

/** Uma barra deitada da Origem; o valor exato está na dica e no texto escondido. */
function OriginBar({
  value,
  width,
  className,
  label,
}: {
  value: Money;
  width: number;
  className: string;
  label: string;
}) {
  return (
    <div className="relative h-2.5">
      <span
        title={`${label}: ${format(value)}`}
        className={cn('absolute inset-y-0 left-0 rounded-full', className)}
        // o valor pequeno ainda aparece: zero é a única barra que some
        style={{ width: value > 0 ? `max(${width}%, 0.375rem)` : 0 }}
      />
      <span className="sr-only">
        {label}: {format(value)}
      </span>
    </div>
  );
}

/** A legenda de bolinhas, embaixo da figura. */
function Legend({ items }: { items: readonly { label: string; className: string }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-texto-suave">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn('size-2 rounded-full', item.className)}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** O ladrilho e a série de cada tom de situação — a cor é o significado. */
const SITUATION_TONES = {
  green: {
    box: 'border-fase-verde/25 bg-fase-verde-suave',
    text: 'text-fase-verde',
    color: 'fase-verde',
  },
  orange: {
    box: 'border-fase-laranja/25 bg-fase-laranja-suave',
    text: 'text-fase-laranja',
    color: 'fase-laranja',
  },
  red: {
    box: 'border-fase-vermelho/25 bg-fase-vermelho-suave',
    text: 'text-fase-vermelho',
    color: 'fase-vermelho',
  },
} as const;

/** Uma coluna de um bloco por situação: o nome, o tom e o lugar dela. */
interface SituationSlot {
  key: string;
  label: string;
  tone: keyof typeof SITUATION_TONES;
}

/** O número de uma situação, quando ele chegou. */
interface Situation {
  key: string;
  label: string;
  count: number;
  /** A linha de baixo do ladrilho — só no painel com `withSupport`. */
  support?: string;
  /** O que vai entre parênteses na legenda — "R$ 156,4 mil". Ausente, só a contagem. */
  legend?: string;
  /** A segunda linha da dica da coluna. */
  tooltip: string;
}

/**
 * Um bloco POR SITUAÇÃO — três ladrilhos tingidos e as colunas embaixo, no
 * desenho do card Boletos das referências. Boletos e Veículos são este mesmo
 * componente (29/10/2026), para os dois não divergirem de anatomia.
 *
 * `situations` nulo é "ainda não chegou": os ladrilhos desenham o esqueleto
 * com o nome de cada situação, que é dado da tela e não da leitura.
 */
function SituationPanel({
  slots,
  situations,
  loading,
  chartTitle,
  chartLabel,
  empty,
  withSupport = true,
}: {
  slots: readonly SituationSlot[];
  situations: readonly Situation[] | null;
  loading: boolean;
  chartTitle: string;
  chartLabel: string;
  empty: string;
  /** Falso, o ladrilho não reserva a linha de apoio — nem no carregamento. */
  withSupport?: boolean;
}) {
  const toneOf = (key: string) => SITUATION_TONES[slots.find((s) => s.key === key)!.tone];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        {slots.map((slot) => {
          const situation = situations?.find((s) => s.key === slot.key);
          const tone = SITUATION_TONES[slot.tone];
          return (
            <div
              key={slot.key}
              className={cn('rounded-xl border px-4 py-3 text-center', tone.box)}
            >
              <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-texto-suave">
                {slot.label}
              </p>
              {loading || situation === undefined ? (
                <Pending className="mx-auto mt-1.5 h-6 w-12" />
              ) : (
                <p className={cn('mt-0.5 text-xl font-bold tabular-nums', tone.text)}>
                  {situation.count.toLocaleString('pt-BR')}
                </p>
              )}
              {withSupport && (
                <p className="mt-0.5 text-xs text-texto-suave tabular-nums">
                  {loading || situation === undefined ? ' ' : situation.support}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <Chart<Situation>
        bare
        withoutTable
        kind="barra"
        stacked
        title={chartTitle}
        label={chartLabel}
        data={situations ?? []}
        loading={loading || situations === null}
        empty={empty}
        category={(s) => s.label}
        tooltip={({ item }) => (
          <>
            <p className="font-semibold text-texto">{item.label}</p>
            <p className="mt-0.5 text-texto-suave tabular-nums">{item.tooltip}</p>
          </>
        )}
        /*
         * Uma série por situação, cada uma só na própria coluna e empilhada:
         * é o jeito de cada coluna ter a sua cor, e a legenda do gráfico
         * vira a linha "Pago: 142 (R$ 28,4 mil)" das referências.
         */
        series={(situations ?? []).map((s) => ({
          key: s.key,
          label: `${s.label}: ${s.count.toLocaleString('pt-BR')}${s.legend === undefined ? '' : ` (${s.legend})`}`,
          color: toneOf(s.key).color,
          value: (item: Situation) => (item.key === s.key ? item.count : null),
        }))}
        height={200}
      />
    </div>
  );
}

const BOLETO_SLOTS: SituationSlot[] = [
  { key: 'paid', label: 'Pago', tone: 'green' },
  { key: 'open', label: 'Aberto', tone: 'orange' },
  { key: 'overdue', label: 'Vencido', tone: 'red' },
];

/**
 * OS BOLETOS da competência, separados por situação.
 *
 * **Pago** é o Extrato gravado (`walletInsights.ticket`: cada boleto uma vez,
 * mesmo quando entrou em duas premiações); **aberto** e **vencido** são os
 * boletos sem pagamento da competência (`walletOpenBoletos`), partidos pelo
 * vencimento contra o dia de hoje — vencido é o que venceu antes de hoje. O
 * valor é o do BOLETO, e não o da premiação: é o que o associado deve.
 *
 * O total do cabeçalho só existe quando os dois lados responderam; somar um
 * lado com nada diria um número de boletos que a carteira não tem.
 */
export function WalletBoletos({
  ticket,
  openBoletos,
  loading,
}: {
  ticket: MeasuredInsights['ticket'] | undefined;
  openBoletos: readonly WalletOpenBoleto[] | undefined;
  loading: boolean;
}) {
  const today = businessDate(new Date());
  const overdue = (b: WalletOpenBoleto) => b.dueDate !== null && b.dueDate < today;
  const situationOf = (
    key: 'paid' | 'open' | 'overdue',
    count: number,
    amount: Money,
  ): Situation => ({
    key,
    label: BOLETO_SLOTS.find((s) => s.key === key)!.label,
    count,
    support: format(amount),
    legend: compactMoney(amount),
    tooltip: `${count.toLocaleString('pt-BR')} boleto(s) · ${format(amount)}`,
  });
  const openOf = (key: 'open' | 'overdue', items: readonly WalletOpenBoleto[]) =>
    situationOf(key, items.length, sum(...items.map((b) => fromColumn(b.amount)!)));

  const situations: Situation[] | null =
    ticket === undefined || openBoletos === undefined
      ? null
      : [
          // o ticket médio saiu daqui em 03/11/2026: mora nos Valores apurados
          situationOf('paid', ticket.boletos, fromColumn(ticket.paid)!),
          openOf(
            'open',
            openBoletos.filter((b) => !overdue(b)),
          ),
          openOf('overdue', openBoletos.filter(overdue)),
        ];
  const total = situations?.reduce((acc, s) => acc + s.count, 0) ?? null;

  return (
    <Card
      title={
        <BlockTitle icon="documento" tone="text-fase-azul">
          Boletos
        </BlockTitle>
      }
      descricao={
        total === null
          ? 'os boletos da competência, separados por situação'
          : `${total.toLocaleString('pt-BR')} boleto(s) na competência · separados por situação`
      }
    >
      <SituationPanel
        slots={BOLETO_SLOTS}
        situations={situations}
        loading={loading}
        chartTitle="Boletos por situação"
        chartLabel="Quantos boletos da competência estão pagos, em aberto e vencidos"
        empty="Nenhum boleto nesta competência."
      />
    </Card>
  );
}

const VEHICLE_SLOTS: SituationSlot[] = [
  { key: 'entered', label: 'Novas Vendas', tone: 'green' },
  { key: 'open', label: 'Inadimplentes', tone: 'orange' },
  { key: 'canceled', label: 'Cancelados', tone: 'red' },
];

/**
 * OS VEÍCULOS da carteira — o que entrou, o que está em aberto e o que já
 * foi cancelado (29/10/2026, pedido do dono, sobre o desenho dos Boletos).
 *
 * - **Novas Vendas** são os contratos do mês do filtro — o mesmo número da
 *   Distribuição da Carteira (`mix.vehicles`).
 * - **Inadimplentes** são os veículos com boleto sem pagamento no mês: os boletos
 *   do card Boletos (aberto + vencido, `walletOpenBoletos`), contados por
 *   PLACA. O boleto sem placa não se junta a nenhum outro e conta como um
 *   veículo — juntá-los pelo associado poria dois carros na mesma conta.
 * - **Cancelados** são os cancelados hoje cuja última alteração no SGA caiu
 *   no mês (`canceledVehicles`). É aproximação, escolhida pelo dono: o
 *   núcleo não guarda quando a situação mudou, e uma edição depois do
 *   cancelamento move o carro de mês.
 *
 * Os ladrilhos não têm linha de apoio (pedido do dono, 29/10/2026): o nome
 * da situação basta.
 *
 * Os três não se somam — o mesmo carro pode ter entrado no mês e estar em
 * aberto —, e por isso o cabeçalho não tem total.
 */
export function WalletVehicles({
  insights,
  openBoletos,
  loading,
}: {
  insights: MeasuredInsights | undefined;
  openBoletos: readonly WalletOpenBoleto[] | undefined;
  loading: boolean;
}) {
  const vehicle = (key: string, count: number): Situation => ({
    key,
    label: VEHICLE_SLOTS.find((s) => s.key === key)!.label,
    count,
    tooltip: `${count.toLocaleString('pt-BR')} veículo(s)`,
  });
  const openVehicles =
    openBoletos === undefined
      ? null
      : new Set(openBoletos.map((b, i) => b.plate ?? `sem-placa-${i}`)).size;

  const situations: Situation[] | null =
    insights === undefined || openVehicles === null
      ? null
      : [
          vehicle('entered', insights.mix.vehicles),
          vehicle('open', openVehicles),
          vehicle('canceled', insights.canceledVehicles),
        ];

  return (
    <Card
      title={
        <BlockTitle icon="veiculo" tone="text-fase-azul">
          Veículos
        </BlockTitle>
      }
    >
      <SituationPanel
        slots={VEHICLE_SLOTS}
        situations={situations}
        loading={loading}
        chartTitle="Veículos por situação"
        chartLabel="Quantos veículos são novas vendas, estão inadimplentes e foram cancelados no mês"
        empty="Nenhum veículo na carteira."
        withSupport={false}
      />
    </Card>
  );
}

/** As cores da distribuição, na ordem das fatias — o "Outros" é sempre cinza. */
const MIX_COLORS = [
  { bg: 'bg-texto', stroke: 'stroke-texto' },
  { bg: 'bg-fase-laranja', stroke: 'stroke-fase-laranja' },
  { bg: 'bg-fase-verde', stroke: 'stroke-fase-verde' },
  { bg: 'bg-fase-azul', stroke: 'stroke-fase-azul' },
  { bg: 'bg-fase-roxo', stroke: 'stroke-fase-roxo' },
] as const;
const MIX_OTHERS = { bg: 'bg-fase-cinza', stroke: 'stroke-fase-cinza' } as const;

type MixTab = 'type' | 'plan' | 'dueDay' | 'brand';

const MIX_TABS: { value: MixTab; label: string }[] = [
  { value: 'type', label: 'Tipo de veículo' },
  { value: 'plan', label: 'Plano' },
  { value: 'dueDay', label: 'Vencimento' },
  { value: 'brand', label: 'Marca' },
];

/**
 * As fatias de uma aba, já com o rótulo da tela. O dia de vencimento chega
 * como `'10'` e vira "Dia 10"; o nulo de cada aba tem o nome do que falta.
 */
function mixSlices(mix: MeasuredInsights['mix'], tab: MixTab): MixSliceShown[] {
  const named = (slices: readonly WalletMixSlice[], missing: string) =>
    slices.map((s) => ({ label: s.label ?? missing, vehicles: s.vehicles }));
  switch (tab) {
    case 'type':
      return named(mix.byType, 'Sem cadastro');
    case 'plan':
      return named(mix.byPlan, 'Sem plano no SGA');
    case 'dueDay':
      return mix.byDueDay.map((s) => ({
        label: s.label === null ? 'Sem boleto' : `Dia ${s.label}`,
        vehicles: s.vehicles,
      }));
    case 'brand':
      return named(mix.byBrand, 'Sem cadastro');
  }
}

/** Uma fatia como a tela a desenha: o rótulo pronto e a contagem. */
interface MixSliceShown {
  label: string;
  vehicles: number;
}

/**
 * A DISTRIBUIÇÃO DA CARTEIRA — os contratos do mês das Métricas, por tipo de
 * veículo, plano e marca (`walletInsights.mix`). Até 29/10/2026 eram os doze
 * meses que terminam nele; o dono pediu que ela seguisse o filtro.
 *
 * As três pizzas saíram em 29/10/2026 e voltaram no mesmo dia, como uma rosca
 * com abas, no desenho das referências. **Vencimento** entrou no mesmo dia: o
 * dia do vencimento original do boleto mais recente de cada veículo
 * (`mix.byDueDay`). O **Plano** ficou ao lado, pedido do dono, mesmo com o SGA
 * mandando `codigo_grupo_produto = 0` em quase todo contrato desde dezembro de
 * 2025 — por isso o nulo dele diz "Sem plano no SGA", e não "Sem cadastro".
 *
 * As cinco maiores fatias têm cor; o resto vira "Outros", em cinza, como no
 * `Chart`. O rótulo nulo é "o cadastro não diz", e aparece com esse nome.
 */
export function WalletDistribution({
  mix,
  loading,
}: {
  mix: MeasuredInsights['mix'] | undefined;
  loading: boolean;
}) {
  const [tab, setTab] = useState<MixTab>('type');

  return (
    <Card
      title={
        <BlockTitle icon="percentual" tone="text-fase-laranja">
          Distribuição da Carteira
        </BlockTitle>
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as MixTab)}>
        {/*
         * A pílula das referências: o cinza mora num invólucro porque o
         * `rolagem-lateral` da lista pinta as pontas com a superfície branca
         * (o aviso de rolagem), e por cima do cinza isso parecia um desbotado.
         * Três abas curtas não rolam.
         */}
        <div className="rounded-xl bg-superficie-suave p-1">
          <TabsList className="gap-1 border-0 p-0 [background:none]">
            {MIX_TABS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="flex-1 rounded-lg border-b-0 py-2 data-[state=active]:bg-superficie data-[state=active]:shadow-sm"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {MIX_TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            {loading || mix === undefined ? (
              <Pending className="h-40 w-full" />
            ) : (
              <MixBreakdown slices={mixSlices(mix, t.value)} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}

/** A rosca e a lista de uma aba da distribuição. */
function MixBreakdown({ slices }: { slices: readonly MixSliceShown[] }) {
  const total = slices.reduce((acc, s) => acc + s.vehicles, 0);
  if (total === 0) {
    return (
      <p className="grid h-32 place-items-center text-center text-sm text-texto-suave">
        Nenhum contrato da carteira neste mês.
      </p>
    );
  }

  const sorted = [...slices].sort((a, b) => b.vehicles - a.vehicles);
  const top = sorted.slice(0, MIX_COLORS.length);
  const rest = sorted.slice(MIX_COLORS.length).reduce((acc, s) => acc + s.vehicles, 0);
  const rows = [
    ...top.map((s, i) => ({
      key: s.label,
      label: s.label,
      vehicles: s.vehicles,
      color: MIX_COLORS[i]!,
    })),
    ...(rest > 0
      ? [{ key: 'outros', label: 'Outros', vehicles: rest, color: MIX_OTHERS }]
      : []),
  ];

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      <Donut
        size={156}
        thickness={24}
        segments={rows.map((r) => ({
          key: r.key,
          value: r.vehicles,
          stroke: r.color.stroke,
        }))}
      />
      <ul className="flex w-full min-w-0 flex-1 flex-col gap-3">
        {rows.map((r) => (
          <li key={r.key}>
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="flex min-w-0 items-center gap-2 text-texto">
                <span
                  aria-hidden="true"
                  className={cn('size-2 shrink-0 rounded-full', r.color.bg)}
                />
                <span className="truncate">{r.label}</span>
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-texto">
                  {r.vehicles.toLocaleString('pt-BR')}
                </span>{' '}
                <span className="text-texto-suave">({share(r.vehicles, total)})</span>
              </span>
            </div>
            <div
              aria-hidden="true"
              className="mt-1 h-1.5 rounded-full bg-superficie-suave"
            >
              <div
                className={cn('h-full rounded-full', r.color.bg)}
                style={{ width: `${(r.vehicles / total) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
